import { useState } from "react";
import { toast } from "sonner";
import { UserPlus, Copy, KeyRound, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useFetch } from "@/hooks/useFetch";
import { ROLES, MODULE_LABELS, fmtDateTime } from "@/lib/format";
import { StatusBadge } from "@/components/StatusBadge";
import { Card, Field, PageHeader, Sel } from "@/components/Field";
import { FormDialog, useSubmit } from "@/components/dialogs/FormDialog";

const roleOpts = Object.entries(ROLES).map(([value, label]) => ({ value, label }));

const copy = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    toast.success("Link copiato");
  } catch {
    window.prompt("Copia il link:", text);
  }
};

const LinkBox = ({ link, testId }) => link && (
  <div data-testid={testId} className="rounded-lg border border-green-200 bg-green-50 p-3 space-y-2">
    <p className="text-sm text-green-900">Invia questo link alla persona (valido 7 giorni). L'invio automatico via e-mail non è ancora attivo.</p>
    <div className="flex gap-2"><Input readOnly value={link} className="h-10 text-xs" /><Button type="button" className="h-10" onClick={() => copy(link)}><Copy className="h-4 w-4" /></Button></div>
  </div>
);

const InviteDialog = ({ open, onOpenChange, onDone }) => {
  const [f, setF] = useState({ email: "", role: "member", modules: [] });
  const [link, setLink] = useState("");
  const { saving, run } = useSubmit();
  const toggle = (m) => setF((s) => ({ ...s, modules: s.modules.includes(m) ? s.modules.filter((x) => x !== m) : [...s.modules, m] }));
  const submit = () => run(async () => {
    const { data } = await api.post("/members/invite", f);
    setLink(data.invite_link);
    onDone();
  }, "Invito creato");
  const change = (o) => { if (!o) { setLink(""); setF({ email: "", role: "member", modules: [] }); } onOpenChange(o); };
  return (
    <FormDialog open={open} onOpenChange={change} title="Invita un membro" onSubmit={submit} saving={saving} testId="invite-dialog" submitLabel="Crea invito">
      <Field label="E-mail *"><Input data-testid="invite-email-input" type="email" required value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className="h-11" /></Field>
      <Field label="Ruolo"><Sel testId="invite-role-select" value={f.role} onChange={(v) => setF({ ...f, role: v })} options={roleOpts} /></Field>
      {f.role === "manager" && (
        <Field label="Moduli gestiti" hint="Se non scegli nulla, il responsabile gestisce tutti i moduli operativi">
          <div className="grid grid-cols-2 gap-2">{Object.entries(MODULE_LABELS).map(([k, l]) => <label key={k} className="flex items-center gap-2 text-sm"><Checkbox data-testid={`invite-module-${k}`} checked={f.modules.includes(k)} onCheckedChange={() => toggle(k)} /> {l}</label>)}</div>
        </Field>
      )}
      <LinkBox link={link} testId="invite-link-box" />
    </FormDialog>
  );
};

const CreateDialog = ({ open, onOpenChange, onDone }) => {
  const [f, setF] = useState({ name: "", username: "", password: "", role: "member" });
  const [done, setDone] = useState(null);
  const { saving, run } = useSubmit();
  const submit = () => {
    if (done) { change(false); return Promise.resolve(); }
    return run(async () => {
      const { data } = await api.post("/members/create", f);
      setDone({ username: data.username, password: f.password });
      onDone();
    }, "Membro creato");
  };
  const change = (o) => { if (!o) { setDone(null); setF({ name: "", username: "", password: "", role: "member" }); } onOpenChange(o); };
  return (
    <FormDialog open={open} onOpenChange={change} title="Crea membro con nome utente" description="Per chi non ha o non vuole usare un'e-mail. Comunica tu nome utente e password alla persona." onSubmit={submit} saving={saving} testId="create-member-dialog" submitLabel={done ? "Chiudi" : "Crea membro"}>
      <Field label="Nome e cognome *"><Input data-testid="create-member-name" required minLength={2} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="h-11" /></Field>
      <Field label="Nome utente *" hint="3-30 caratteri: lettere, numeri, punto, trattino"><Input data-testid="create-member-username" required pattern="[a-zA-Z0-9._\-]{3,30}" autoCapitalize="none" value={f.username} onChange={(e) => setF({ ...f, username: e.target.value })} className="h-11" /></Field>
      <Field label="Password iniziale *" hint="Almeno 8 caratteri; la persona potrà cambiarla in Il mio profilo"><Input data-testid="create-member-password" required minLength={8} value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} className="h-11" /></Field>
      <Field label="Ruolo"><Sel testId="create-member-role" value={f.role} onChange={(v) => setF({ ...f, role: v })} options={roleOpts} /></Field>
      {done && <div data-testid="create-member-done" className="rounded-lg border border-green-200 bg-green-50 p-3 text-sm text-green-900">Accesso: nome utente <strong>{done.username}</strong> · password <strong>{done.password}</strong></div>}
    </FormDialog>
  );
};

export default function MembersPage() {
  const { can, user } = useAuth();
  const { data, reload } = useFetch("/members");
  const invites = useFetch(can("members", "create") ? "/members/invitations" : null);
  const [open, setOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [resetLink, setResetLink] = useState("");
  const edit = can("members", "edit");

  const update = async (m, body, msg) => {
    try {
      await api.patch(`/members/${m.membership_id}`, body);
      toast.success(msg);
      reload();
    } catch (e) { toast.error(formatError(e)); }
  };
  const reset = async (m) => {
    try {
      const { data: r } = await api.post(`/members/${m.membership_id}/reset-link`);
      setResetLink(r.reset_link);
    } catch (e) { toast.error(formatError(e)); }
  };
  const revoke = async (i) => {
    try { await api.delete(`/members/invitations/${i.id}`); toast.success("Invito revocato"); invites.reload(); } catch (e) { toast.error(formatError(e)); }
  };

  return (
    <>
      <PageHeader title="Membri e ruoli">
        {can("members", "create") && <Button data-testid="members-create-button" variant="outline" className="h-11" onClick={() => setCreateOpen(true)}><KeyRound className="h-4 w-4 mr-1" /> Crea con nome utente</Button>}
        {can("members", "create") && <Button data-testid="members-invite-button" className="h-11" onClick={() => setOpen(true)}><UserPlus className="h-4 w-4 mr-1" /> Invita membro</Button>}
      </PageHeader>
      <LinkBox link={resetLink} testId="reset-link-box" />
      <Card><ul className="divide-y" data-testid="members-list">
        {data?.map((m) => (
          <li key={m.membership_id} data-testid={`member-row-${m.user_id}`} className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-5 py-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2"><span className="font-semibold text-slate-900">{m.name}</span>{m.status !== "active" && <StatusBadge label="Disattivato" />}{m.user_id === user.id && <StatusBadge label="Tu" tone="info" />}</div>
              <div className="text-sm text-slate-500">{[m.username && `@${m.username}`, m.email].filter(Boolean).join(" · ")}{m.role === "manager" && m.modules && ` · ${m.modules.map((x) => MODULE_LABELS[x]).join(", ")}`}</div>
            </div>
            {edit ? (
              <div className="flex flex-wrap items-center gap-2">
                <div className="w-44"><Sel testId={`member-role-${m.user_id}`} value={m.role} onChange={(v) => update(m, { role: v }, "Ruolo aggiornato")} options={roleOpts} /></div>
                {m.role !== "admin" && can("documents", "view") && (
                  <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer" title="Può caricare ed eliminare documenti">
                    <Checkbox data-testid={`member-docs-${m.user_id}`} checked={!!m.docs_manager} onCheckedChange={(c) => update(m, { docs_manager: !!c }, c ? "Può gestire i documenti" : "Permesso documenti rimosso")} /> Gestisce documenti
                  </label>
                )}
                <Button data-testid={`member-reset-${m.user_id}`} variant="outline" className="h-11" onClick={() => reset(m)} title="Link reimpostazione password"><KeyRound className="h-4 w-4" /></Button>
                {m.user_id !== user.id && <Button data-testid={`member-toggle-${m.user_id}`} variant="outline" className="h-11" onClick={() => update(m, { status: m.status === "active" ? "disabled" : "active" }, "Stato aggiornato")}>{m.status === "active" ? "Disattiva" : "Riattiva"}</Button>}
              </div>
            ) : <StatusBadge label={m.role_label} tone="info" />}
          </li>
        ))}
      </ul></Card>
      {invites.data?.length > 0 && (
        <Card testId="pending-invitations">
          <h2 className="font-heading font-semibold px-5 h-12 flex items-center border-b">Inviti in attesa</h2>
          <ul className="divide-y">{invites.data.map((i) => (
            <li key={i.id} className="flex items-center justify-between gap-3 px-5 py-3 text-sm">
              <span>{i.email} · {ROLES[i.role]} · {fmtDateTime(i.created_at)}</span>
              <Button data-testid={`invite-revoke-${i.id}`} variant="ghost" className="h-10 text-red-700" onClick={() => revoke(i)}><Trash2 className="h-4 w-4 mr-1" /> Revoca</Button>
            </li>))}
          </ul>
        </Card>
      )}
      <InviteDialog open={open} onOpenChange={setOpen} onDone={invites.reload} />
      <CreateDialog open={createOpen} onOpenChange={setCreateOpen} onDone={reload} />
    </>
  );
}
